import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import saccoLoanService from "@saccoLoanService";

const REPAYMENTS_KEY = 'sacco_loan_repayments';
const LOANS_KEY = 'sacco_loans';

/**
 * Fetches all sacco loan repayments for a given loan.
 * @param loanId - The ID of the loan for which to fetch repayments.
 * @returns A query object that can be used to fetch and manage the repayments.
 */
export function useFetchSaccoRepaymentsForLoan(loanId: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [REPAYMENTS_KEY, loanId],
    queryFn: async () => {
      return await saccoLoanService.fetchAllRepaymentsForLoan(user.id, loanId);
    },
    enabled: !!user && !!loanId,
  });
}

/**
 * Marks a sacco loan installment as paid.
 * @param installmentId - The ID of the installment to mark as paid.
 * @param loanId - The ID of the loan associated with the installment.
 * @param paidDate - The date when the installment was paid.
 * @returns A promise that resolves to a SaccoLoanInstallmentPaidResponse indicating the result of marking the installment as paid.
 */
export function useMarkSaccoInstallmentAsPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ installmentId, loanId, paidDate }: { installmentId: string; loanId: string; paidDate: string }) => {
      return await saccoLoanService.markInstallmentPaid({ installmentId, loanId, paidDate })
    },
    onSuccess: ({ loanId }) => {
      qc.invalidateQueries({ queryKey: [REPAYMENTS_KEY, loanId] });
      qc.invalidateQueries({ queryKey: [LOANS_KEY] });
    },
  });
}
